import axios from 'axios';

import { shouldRenewToken, storeUserData, retrieveUserData } from './authorization';
import { onAjaxEnhancer } from 'redux/store.util';

type AxiosRequestHeaders = Record<string, string>;
type DataType<ValueType> = Record<string, ValueType>;

const defaultHeaders: AxiosRequestHeaders = {
  'Content-Type': 'application/json',
};

const client = axios.create({
  baseURL: process.env.REACT_APP_API_URL,
  headers: defaultHeaders,
});

// eslint-disable-next-line no-unused-vars
function composeHeaders(headersRequester?: (headers: AxiosRequestHeaders) => any) {
  let newHeaders: AxiosRequestHeaders = {};

  delete newHeaders.Authorization;

  if (headersRequester) headersRequester({ ...defaultHeaders, ...newHeaders });

  return (theirHeaders?: AxiosRequestHeaders) => {
    const augmentedHeaders = {
      ...defaultHeaders,
      ...newHeaders,
      ...theirHeaders,
    };

    return augmentedHeaders;
  };
}

let renewRequest: Promise<any>;
let renewRequestIsPending: boolean = false;

function refreshToken() {
  const composedHeaders = composeHeaders();
  const currentUser = retrieveUserData();

  if (currentUser && shouldRenewToken(currentUser.sessionExpiresAt)) {
    // renew request should be dispatched only the first time token expiration is detected
    // following requests will wait for the renewed token
    if (!renewRequestIsPending) {
      renewRequest = client({
        url: 'session/renew',
        method: 'POST',
        withCredentials: true,
        headers: composedHeaders(),
        data: {},
      });

      renewRequest.then((res) => {
        storeUserData(res.data);
        renewRequestIsPending = false;
      });

      renewRequest.catch((err) => {
        renewRequestIsPending = false;
        return Promise.reject(err);
      });

      renewRequestIsPending = true;
    }

    return renewRequest;
  }

  return Promise.resolve(null);
}

function isUnauthorized(error: any): boolean {
  return error?.response?.status === 401;
}

// the session cookie may have just been rotated by a concurrent renewal
// (old jti blacklisted); on 401, wait for any in-flight renewal and retry
// the request exactly once, rethrowing the ORIGINAL error if the retry also
// fails so the existing global 401 handling (logout) still applies
async function requestWithRetry(config: Record<string, any>) {
  await refreshToken();

  try {
    return await client(config);
  } catch (error) {
    if (!isUnauthorized(error)) throw error;

    try {
      await refreshToken();
      return await client(config);
    } catch {
      throw error;
    }
  }
}

async function getRequest(urlParam: string) {
  const composedHeaders = composeHeaders();

  return requestWithRetry({
    url: urlParam,
    method: 'GET',
    headers: composedHeaders(),
    withCredentials: true,
  });
}

async function getFileRequest(urlParam: string) {
  const composedHeaders = composeHeaders();

  return requestWithRetry({
    url: urlParam,
    method: 'GET',
    headers: composedHeaders(),
    withCredentials: true,
    responseType: 'blob',
  });
}

async function postRequest(urlParam: string, data: any) {
  const composedHeaders = composeHeaders();

  return requestWithRetry({
    url: urlParam,
    method: 'POST',
    headers: composedHeaders(),
    data,
    withCredentials: true,
  });
}

async function putRequest<T>(urlParam: string, data: DataType<T>) {
  const composedHeaders = composeHeaders();

  return requestWithRetry({
    url: urlParam,
    method: 'PUT',
    headers: composedHeaders(),
    data,
    withCredentials: true,
  });
}

async function patchRequest<T>(urlParam: string, data: DataType<T>) {
  const composedHeaders = composeHeaders();

  return requestWithRetry({
    url: urlParam,
    method: 'PATCH',
    headers: composedHeaders(),
    data,
    withCredentials: true,
  });
}

async function deleteRequest(urlParam: string) {
  const composedHeaders = composeHeaders();

  return requestWithRetry({
    url: urlParam,
    method: 'DELETE',
    headers: composedHeaders(),
    withCredentials: true,
  });
}

type VoidFunction = () => void;

async function promisify<Success, Error>(
  promise: Promise<any>,
  payload?: {
    onSuccess?: VoidFunction;
    onError?: VoidFunction;
    onFinish?: VoidFunction;
    [key: string]: any;
  }
): Promise<[Success, Error]> {
  try {
    const success = await promise;
    if (payload) {
      onAjaxEnhancer(payload, ['onSuccess', 'onFinish']);
    }
    return [success, null as unknown as Error];
  } catch (error) {
    if (payload) {
      onAjaxEnhancer(payload, ['onError', 'onFinish']);
    }

    window.dispatchEvent(new ErrorEvent('error', {
      error
    }));

    return [null as unknown as Success, error as Error];
  }
}

export { getRequest, getFileRequest, postRequest, putRequest, patchRequest, deleteRequest, promisify };
